import type {
  AddNewTaskRequest,
  CalendarRecord,
  CreateNewProjectRequest,
} from '../api/schedulingApis';
import type { TaskSnapshot, LinkSnapshot } from '../stores/types';
import { parseDateSafe, resolveTaskName, toWallClockDateTime } from '../utils/utils';

export interface NewTaskFormValues {
  name: string;
  start: Date;
  finish: Date;
  duration: number;
  milestone: boolean;
  summary: boolean;
  taskType: string;
  /** Comma-separated predecessor refs (WBS code, task name, or task id) — same as grid inline edit. */
  predecessors: string;
  /** Dependency type applied to each resolved predecessor link (FS/SS/FF/SF). */
  dependencyType: 'FS' | 'SS' | 'FF' | 'SF';
  /** Lag (days); negative = lead. Applied when typed predecessors omit an explicit lag. */
  lag: number;
}

/** ISO-8601 instants for create-task API (Micronaut/Jackson Instant deserialization). */
export function toCreateTaskDateTime(date: Date | string): string {
  const d = date instanceof Date ? date : new Date(String(date).replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return new Date().toISOString();
  return d.toISOString();
}

/** Normalize DateInput / API values to a local wall-clock Date for schedule math. */
function toLocalScheduleDate(value: Date | string | null | undefined, fallback?: Date): Date {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return new Date(value.getTime());
  }
  if (value != null && String(value).trim()) {
    const parsed = parseDateSafe(String(value));
    if (parsed && !Number.isNaN(parsed.getTime())) {
      return parsed;
    }
  }
  if (fallback instanceof Date && !Number.isNaN(fallback.getTime())) {
    return new Date(fallback.getTime());
  }
  return new Date();
}

function isWorkingDay(date: Date): boolean {
  const day = date.getDay();
  return day !== 0 && day !== 6;
}

/** Finish date from start + duration in working days (Mon–Fri), matching dhtmlx work_time. */
export function computeFinishFromStartAndDuration(
  start: Date | string,
  durationDays: number
): Date {
  const base = toLocalScheduleDate(start);
  const days = Math.max(0, Math.round(Number(durationDays) || 0));
  const cursor = new Date(base.getFullYear(), base.getMonth(), base.getDate());
  if (days === 0) return cursor;

  let counted = 0;
  while (counted < days) {
    if (isWorkingDay(cursor)) {
      counted += 1;
      if (counted === days) break;
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  return cursor;
}

/** Working-day duration between start and finish (inclusive), matching dhtmlx work_time. */
export function computeDurationFromStartAndFinish(
  start: Date | string,
  finish: Date | string
): number {
  const safeStart = toLocalScheduleDate(start);
  const safeFinish = toLocalScheduleDate(finish, safeStart);
  const startDay = new Date(safeStart.getFullYear(), safeStart.getMonth(), safeStart.getDate());
  const finishDay = new Date(safeFinish.getFullYear(), safeFinish.getMonth(), safeFinish.getDate());
  if (finishDay < startDay) return 0;

  let count = 0;
  const cursor = new Date(startDay);
  while (cursor <= finishDay) {
    if (isWorkingDay(cursor)) count += 1;
    cursor.setDate(cursor.getDate() + 1);
  }
  return count;
}

export function coerceNewTaskFormValues(form: NewTaskFormValues): NewTaskFormValues {
  const safeStart = toLocalScheduleDate(form.start);
  const safeFinish = toLocalScheduleDate(form.finish, safeStart);
  const duration = Math.max(0, Math.round(Number(form.duration) || 0));
  const lag = Math.trunc(Number(form.lag) || 0);
  const dependencyType =
    form.dependencyType === 'SS' ||
    form.dependencyType === 'FF' ||
    form.dependencyType === 'SF'
      ? form.dependencyType
      : 'FS';
  return {
    ...form,
    start: safeStart,
    finish: safeFinish,
    duration: duration > 0 ? duration : computeDurationFromStartAndFinish(safeStart, safeFinish),
    summary: false,
    dependencyType,
    lag,
  };
}

export function createClientTaskIds(): { id: string; uniqueId: string } {
  const id =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `task-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  const uniqueId =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `uid-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  return { id, uniqueId };
}

export function resolveDefaultCalendarId(tasks: TaskSnapshot[]): string {
  for (const t of tasks) {
    const cal = t.calendarId?.trim();
    if (cal) return cal;
  }
  return '';
}

/** Pick default calendar for a project from the calendar list API. */
export function pickProjectCalendarId(calendars: CalendarRecord[]): string {
  if (!calendars.length) return '';
  const picked = calendars.find((c) => c.isDefault) ?? calendars[0];
  return picked?.id?.trim() ?? '';
}

/** Sync calendar resolution from already-loaded data (no network). */
export function resolveCalendarIdSync(params: {
  parent?: TaskSnapshot | null;
  tasks: TaskSnapshot[];
  projectCalendarId?: string;
}): string {
  return (
    params.parent?.calendarId?.trim() ||
    resolveDefaultCalendarId(params.tasks) ||
    params.projectCalendarId?.trim() ||
    ''
  );
}

/**
 * Resolve calendar for a new task: parent → any loaded task → cached project calendar → API list.
 * Prefer {@link resolveCalendarIdSync} on interactive paths (modals) to avoid blocking the UI.
 */
export async function resolveCalendarIdForNewTask(params: {
  projectId: string;
  parent?: TaskSnapshot | null;
  tasks: TaskSnapshot[];
  projectCalendarId?: string;
  fetchProjectCalendars: (projectId: string) => Promise<CalendarRecord[]>;
}): Promise<string> {
  const cached = resolveCalendarIdSync({
    parent: params.parent,
    tasks: params.tasks,
    projectCalendarId: params.projectCalendarId,
  });
  if (cached) return cached;

  const calendars = await params.fetchProjectCalendars(params.projectId);
  return pickProjectCalendarId(calendars);
}

export function taskHasChildren(taskId: string, tasks: TaskSnapshot[]): boolean {
  const id = String(taskId);
  return tasks.some((t) => String(t.parentTaskId ?? '') === id);
}

/**
 * Backend-only bootstrap shell: outline level 0 with a level-1 child pointing at it.
 * Do NOT treat every level-0 task as hidden — existing schedules use level 0 as the visible root.
 */
export function isPlanningShellTask(
  task: TaskSnapshot,
  allTasks: TaskSnapshot[],
): boolean {
  if (Number(task.outlineLevel) !== 0) {
    return false;
  }
  return allTasks.some(
    (t) =>
      Number(t.outlineLevel) === 1 &&
      String(t.parentTaskId ?? '') === String(task.id),
  );
}

/** Tasks shown in the Gantt grid (excludes bootstrap shell rows only). */
export function isUiVisibleTask(task: TaskSnapshot, allTasks: TaskSnapshot[]): boolean {
  return !isPlanningShellTask(task, allTasks);
}

/** True when the schedule has at least one task visible in the UI (O(n), single pass). */
export function projectHasUiRootTask(tasks: TaskSnapshot[]): boolean {
  if (tasks.length === 0) return false;
  const shellIds = new Set<string>();
  for (const t of tasks) {
    if (Number(t.outlineLevel) === 1 && t.parentTaskId) {
      shellIds.add(String(t.parentTaskId));
    }
  }
  for (const t of tasks) {
    if (Number(t.outlineLevel) === 0 && shellIds.has(String(t.id))) {
      continue;
    }
    return true;
  }
  return false;
}

/**
 * Detect IDB cache that only contains the empty-Gantt auto-seed (shell + location UI root).
 * Those caches must not block a server refetch — they can hide a real schedule.
 */
export function isLikelyAutoSeedOnlySchedule(tasks: TaskSnapshot[]): boolean {
  if (tasks.length === 0 || tasks.length > 2) return false;
  const nonShell = tasks.filter((t) => !isPlanningShellTask(t, tasks));
  if (nonShell.length !== 1) return false;
  const root = nonShell[0];
  return (
    String(root.taskType ?? '')
      .trim()
      .toLowerCase() === 'location' && Number(root.outlineLevel) === 1
  );
}

/** @deprecated Use {@link projectHasUiRootTask}. */
export function projectHasRootTask(tasks: TaskSnapshot[]): boolean {
  return projectHasUiRootTask(tasks);
}

/** dhtmlx parent: level-1 UI root has no visible parent even though it links to level 0. */
export function getDhtmlxParentId(
  task: TaskSnapshot,
  taskById: Map<string, TaskSnapshot>,
): string | undefined {
  const parentId = task.parentTaskId?.trim();
  if (!parentId) return undefined;
  const parent = taskById.get(parentId);
  if (parent && isPlanningShellTask(parent, Array.from(taskById.values()))) {
    return undefined;
  }
  return parentId;
}

function computeOutlinePosition(
  parentTaskId: string | null,
  tasks: TaskSnapshot[],
): number {
  const siblings = tasks.filter((t) => {
    const p = t.parentTaskId;
    if (!parentTaskId) {
      return p == null || p === '' || p === '0';
    }
    return String(p ?? '') === String(parentTaskId);
  });
  if (siblings.length === 0) return 0;
  const maxPos = Math.max(...siblings.map((t) => Number(t.outlinePosition ?? 0)));
  return maxPos + 1;
}

function pickPlanningField(
  parent: TaskSnapshot | null | undefined,
  tasks: TaskSnapshot[],
  key: 'taskMode' | 'constraintType' | 'planningType' | 'durationType',
  fallback: string,
): string {
  if (parent?.[key]) return String(parent[key]);
  for (const t of tasks) {
    if (t[key]) return String(t[key]);
  }
  return fallback;
}

/** Drop null/undefined keys so Jackson does not fail on optional date/enum fields. */
export function sanitizeCreateTaskPayload(
  payload: AddNewTaskRequest,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(payload)) {
    if (value === null || value === undefined) continue;
    if (key === 'meta' && typeof value === 'object') {
      const meta: Record<string, unknown> = {};
      for (const [mk, mv] of Object.entries(value as Record<string, unknown>)) {
        if (mv !== null && mv !== undefined) meta[mk] = mv;
      }
      if (Object.keys(meta).length > 0) out.meta = meta;
      continue;
    }
    if (typeof value === 'string' && value === '' && key !== 'notes') continue;
    out[key] = value;
  }
  return out;
}

export function buildAddNewTaskRequest(params: {
  projectId: string;
  parentTaskId: string | null;
  form: NewTaskFormValues;
  calendarId?: string;
  tasks: TaskSnapshot[];
  parent?: TaskSnapshot | null;
}): AddNewTaskRequest {
  const { id, uniqueId } = createClientTaskIds();
  const { projectId, parentTaskId, form, calendarId, tasks, parent: parentArg } = params;
  const parent =
    parentArg ??
    (parentTaskId
      ? tasks.find((t) => String(t.id) === String(parentTaskId)) ?? null
      : null);
  const normalizedForm = coerceNewTaskFormValues(form);
  const start = toCreateTaskDateTime(normalizedForm.start);
  const finish = toCreateTaskDateTime(normalizedForm.finish);
  const now = toCreateTaskDateTime(new Date());
  const outlineLevel =
    parent != null ? Math.max(0, Number(parent.outlineLevel ?? 0) + 1) : 0;
  const outlinePosition = computeOutlinePosition(parent?.id ?? parentTaskId, tasks);
  const duration = Math.max(0, Number(normalizedForm.duration) || 0);
  const taskTypeMeta = normalizedForm.taskType || 'unknown';
  const resolvedCalendarId = calendarId?.trim();

  const payload: AddNewTaskRequest = {
    id,
    projectId,
    uniqueId,
    name: normalizedForm.name.trim(),
    outlineLevel,
    outlinePosition,
    taskMode: pickPlanningField(parent, tasks, 'taskMode', 'AutoScheduled'),
    milestone: normalizedForm.milestone,
    summary: normalizedForm.summary,
    duration,
    start,
    finish,
    slack: 0,
    critical: false,
    work: 0,
    cost: 0,
    actualDuration: 0,
    actualWork: 0,
    actualCost: 0,
    remainingDuration: duration,
    remainingWork: 0,
    creationDate: now,
    percentComplete: 0,
    workPercentComplete: 0,
    physicalPercentComplete: 0,
    fixedCost: 0,
    priority: 500,
    constraintType: pickPlanningField(parent, tasks, 'constraintType', 'ASAP'),
    type: pickPlanningField(parent, tasks, 'planningType', 'FixedDuration'),
    active: true,
    durationType: pickPlanningField(parent, tasks, 'durationType', 'Fixed'),
    notes: '',
    meta: {
      type: taskTypeMeta,
    },
  };

  const locationId = parent?.locationId?.trim();
  const resolvedParentId = parent?.id ?? parentTaskId;
  if (resolvedParentId) {
    payload.parentTaskId = String(resolvedParentId);
  }

  if (resolvedCalendarId) {
    payload.calendarId = resolvedCalendarId;
  }

  if (locationId) {
    payload.locationId = locationId;
    payload.meta = { ...payload.meta, locationId };
  }

  return payload;
}

export function buildCreateNewProjectRequest(params: {
  projectId: string;
  title: string;
  projectStart: Date | string;
  projectFinish: Date | string;
}): CreateNewProjectRequest {
  return {
    id: params.projectId,
    title: params.title.trim(),
    statusDate: new Date().toISOString(),
    projectStart: toCreateTaskDateTime(params.projectStart),
    projectFinish: toCreateTaskDateTime(params.projectFinish),
    minutesPerDay: 480,
    minutesPerWeek: 0,
    daysPerMonth: 0,
    currencyCode: 'USD',
    currencySymbol: '$',
    currencyDigits: 2,
    baselineForEV: 0,
    meta: {},
  };
}

/**
 * First schedule bootstrap: project POST + level-0 shell + level-1 UI root (same task details).
 */
export function buildInitialRootTaskSetup(params: {
  projectId: string;
  form: NewTaskFormValues;
  calendarId?: string;
  projectTitle?: string;
}): {
  project: CreateNewProjectRequest;
  level0: AddNewTaskRequest;
  level1: AddNewTaskRequest;
} {
  const normalizedForm = coerceNewTaskFormValues(params.form);
  const title = params.projectTitle?.trim() || normalizedForm.name.trim();
  const project = buildCreateNewProjectRequest({
    projectId: params.projectId,
    title,
    projectStart: normalizedForm.start,
    projectFinish: normalizedForm.finish,
  });

  const level0Ids = createClientTaskIds();
  const level1Ids = createClientTaskIds();

  const level0 = buildAddNewTaskRequest({
    projectId: params.projectId,
    parentTaskId: null,
    form: params.form,
    calendarId: params.calendarId,
    tasks: [],
    parent: null,
  });
  level0.id = level0Ids.id;
  level0.uniqueId = level0Ids.uniqueId;
  level0.outlineLevel = 0;
  level0.outlinePosition = 0;
  delete (level0 as { parentTaskId?: string }).parentTaskId;

  const level1 = buildAddNewTaskRequest({
    projectId: params.projectId,
    parentTaskId: level0.id,
    form: params.form,
    calendarId: params.calendarId,
    tasks: [],
    parent: taskSnapshotFromCreateRequest(level0),
  });
  level1.id = level1Ids.id;
  level1.uniqueId = level1Ids.uniqueId;
  level1.outlineLevel = 1;
  level1.outlinePosition = 1;
  level1.parentTaskId = level0.id;

  return { project, level0, level1 };
}

/** Find the level-0 shell paired with a level-1 UI root. */
export function findPlanningShellForUiRoot(
  uiRoot: TaskSnapshot,
  tasks: TaskSnapshot[],
): TaskSnapshot | undefined {
  const parentId = uiRoot.parentTaskId?.trim();
  if (!parentId) return undefined;
  const parent = tasks.find((t) => String(t.id) === parentId);
  return parent && isPlanningShellTask(parent, tasks) ? parent : undefined;
}

/** Build a store snapshot for a task that exists only locally until Save to Server. */
export function taskSnapshotFromCreateRequest(
  req: AddNewTaskRequest,
  opts?: {
    tasks?: TaskSnapshot[];
    parent?: TaskSnapshot | null;
  },
): TaskSnapshot {
  const taskType =
    (req.meta && typeof req.meta.type === 'string' ? req.meta.type : null) ?? 'unknown';
  const locationId =
    req.locationId ??
    (req.meta && typeof req.meta.locationId === 'string' ? req.meta.locationId : null) ??
    null;

  const parentTaskId = req.parentTaskId?.trim() ? req.parentTaskId : null;
  const tasks = opts?.tasks ?? [];
  const parent =
    opts?.parent !== undefined
      ? opts.parent
      : parentTaskId
        ? tasks.find((t) => String(t.id) === String(parentTaskId)) ?? null
        : null;

  return {
    id: req.id,
    projectId: req.projectId,
    parentTaskId,
    taskName: req.name,
    wbs: null,
    /** Provisional until Save to Server reloads API-assigned Sr No. */
    srNo: predictProvisionalSrNo(parent, tasks, parentTaskId),
    outlineLevel: req.outlineLevel,
    outlinePosition: req.outlinePosition,
    critical: req.critical ?? false,
    milestone: req.milestone,
    summary: req.summary,
    start: toWallClockDateTime(req.start) ?? req.start,
    finish: toWallClockDateTime(req.finish) ?? req.finish,
    duration: req.duration,
    percentComplete: req.percentComplete ?? 0,
    baselineInfo: [],
    taskType,
    locationType: null,
    locationId,
    calendarId: req.calendarId ?? null,
    uniqueId: req.uniqueId,
    taskMode: req.taskMode ?? null,
    constraintType: req.constraintType ?? null,
    planningType: req.type ?? null,
    durationType: req.durationType ?? null,
    auto_scheduling: true,
    readonly: false,
  };
}

/**
 * Local-only Sr No for new tasks before the server assigns one.
 * Backend `sr_no` is a flat integer (ROW_NUMBER over outline path), so provisional
 * values use max(numeric srNo)+1 — not hierarchical parent.child codes.
 */
export function predictProvisionalSrNo(
  _parent: TaskSnapshot | null | undefined,
  tasks: TaskSnapshot[],
  _parentTaskId: string | null,
): string {
  let max = -1;
  for (const t of tasks) {
    const raw = t.srNo != null ? String(t.srNo).trim() : '';
    if (!/^\d+$/.test(raw)) continue;
    max = Math.max(max, Number(raw));
  }
  return String(max + 1);
}

/** Apply latest grid/drawer edits onto the pending create payload before save. */
export function mergeCreatePayloadWithSnapshot(
  payload: AddNewTaskRequest,
  snapshot: TaskSnapshot,
): AddNewTaskRequest {
  const name = resolveTaskName(snapshot) || payload.name;
  const startDate = snapshot.start ? parseDateSafe(snapshot.start) : null;
  const finishDate = snapshot.finish ? parseDateSafe(snapshot.finish) : null;
  return {
    ...payload,
    name,
    taskName: name,
    start: startDate ? toCreateTaskDateTime(startDate) : payload.start,
    finish: finishDate ? toCreateTaskDateTime(finishDate) : payload.finish,
    duration: snapshot.duration ?? payload.duration,
    milestone: snapshot.milestone,
    summary: snapshot.summary,
    percentComplete: snapshot.percentComplete ?? payload.percentComplete,
    meta: {
      ...payload.meta,
      type: snapshot.taskType ?? payload.meta?.type ?? 'unknown',
      ...(snapshot.locationId ? { locationId: snapshot.locationId } : {}),
    },
    ...(snapshot.locationId ? { locationId: snapshot.locationId } : {}),
  };
}

export function defaultNewTaskFormValues(parent?: TaskSnapshot | null): NewTaskFormValues {
  if (parent) {
    const safeStart = toLocalScheduleDate(parent.start);
    const safeFinish = toLocalScheduleDate(parent.finish, safeStart);
    const parentDuration = Math.max(0, Math.round(Number(parent.duration) || 0));
    const duration =
      parentDuration > 0
        ? parentDuration
        : computeDurationFromStartAndFinish(safeStart, safeFinish);
    return {
      name: '',
      start: safeStart,
      finish: safeFinish,
      duration,
      milestone: false,
      summary: false,
      taskType: 'construction_activity',
      predecessors: '',
      dependencyType: 'FS',
      lag: 0,
    };
  }

  const safeStart = toLocalScheduleDate(null);
  const duration = 1;
  const finish = computeFinishFromStartAndDuration(safeStart, duration);
  return {
    name: '',
    start: safeStart,
    finish,
    duration,
    milestone: false,
    summary: false,
    taskType: 'construction_activity',
    predecessors: '',
    dependencyType: 'FS',
    lag: 0,
  };
}

/** Normalize optional task field values for string comparison. */
function toComparableString(value: unknown): string {
  if (value == null) return '';
  return String(value).trim();
}

/** Split predecessor cell text into lookup tokens (strips FS/SS/FF/SF suffixes). */
export function parsePredecessorInput(input: string): string[] {
  return input
    .split(/[,;]+/)
    .map((part) => part.trim())
    .filter(Boolean)
    .map((token) => token.replace(/\s*(FS|SS|FF|SF)\s*$/i, '').trim())
    .filter(Boolean);
}

/** Resolve predecessor tokens to task ids using WBS, srNo, name, or id. */
export function resolvePredecessorTaskIds(
  tokens: string[],
  tasks: TaskSnapshot[],
  resolveWbsCode?: (taskId: string) => string | null,
): string[] {
  const resolved: string[] = [];
  const seen = new Set<string>();

  const matchesSrNo = (task: TaskSnapshot, token: string): boolean => {
    const sr = toComparableString(task.srNo);
    const tok = toComparableString(token);
    if (!sr || !tok) return false;
    if (sr === tok) return true;
    if (sr.toLowerCase() === tok.toLowerCase()) return true;
    return false;
  };

  for (const token of tokens) {
    const lower = token.toLowerCase();
    const match = tasks.find((task) => {
      if (String(task.id) === token) return true;
      if (matchesSrNo(task, token)) return true;
      if (toComparableString(task.wbs) === token) return true;
      if (resolveTaskName(task).toLowerCase() === lower) return true;
      if (resolveWbsCode) {
        const wbs = toComparableString(resolveWbsCode(task.id));
        if (wbs && (wbs === token || wbs.toLowerCase() === lower)) return true;
      }
      return false;
    });
    if (match && !seen.has(match.id)) {
      seen.add(match.id);
      resolved.push(match.id);
    }
  }

  return resolved;
}

/**
 * Next local gantt link id (numeric). Server dependency PKs are UUIDs — never
 * POST this value as `createTaskDependency.id` (omit id and let the DB generate).
 */
export function nextClientLinkId(existingLinks: LinkSnapshot[]): number {
  let max = 0;
  for (const link of existingLinks) {
    const n = typeof link.id === 'number' ? link.id : Number(link.id);
    if (Number.isFinite(n) && n > max) {
      max = n;
    }
  }
  return max + 1;
}

/** Collect parent chain ids so branches stay expanded after adding a child. */
export function collectAncestorTaskIds(
  parentTaskId: string | null | undefined,
  tasks: TaskSnapshot[],
): string[] {
  const ids: string[] = [];
  const byId = new Map(tasks.map((t) => [String(t.id), t]));
  let current = parentTaskId != null ? toComparableString(parentTaskId) || null : null;

  while (current) {
    ids.push(current);
    current = toComparableString(byId.get(current)?.parentTaskId) || null;
  }

  return ids;
}
